import { useState, useCallback } from 'react';
import axios from 'axios';

interface UseApiCallOptions<T = unknown> {
  onSuccess?: (data: T) => void;
  onError?: (error: Error | unknown) => void;
  showLoading?: boolean;
  showError?: boolean;
}

interface UseApiCallReturn {
  isLoading: boolean;
  error: string | null;
  execute: <T>(apiCall: () => Promise<T>) => Promise<T>;
  clearError: () => void;
}

export function useApiCall(options: UseApiCallOptions = {}): UseApiCallReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async <T>(apiCall: () => Promise<T>): Promise<T> => {
    if (options.showLoading !== false) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const result = await apiCall();
      if (options.onSuccess) {
        options.onSuccess(result);
      }
      return result;
    } catch (err: unknown) {
      let errorMessage = 'An unexpected error occurred';

      // Prefer server-provided error message if available (Axios errors)
      if (axios.isAxiosError(err)) {
        const serverData = err.response?.data as unknown;
        if (serverData && typeof serverData === 'object') {
          // Check for error field first (common in API responses)
          if ('error' in serverData) {
            errorMessage = String((serverData as { error: unknown }).error ?? errorMessage);
          } else if ('message' in serverData) {
            errorMessage = String((serverData as { message: unknown }).message ?? errorMessage);
          }
        }
        if (errorMessage === 'An unexpected error occurred' && typeof err.message === 'string' && err.message) {
          errorMessage = err.message;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        // Fallback for non-Axios errors that still include a message field
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errorMessage = String((err as any).message ?? errorMessage);
      }
      
      setError(errorMessage);
      
      if (options.onError) {
        options.onError(err);
      }
      
      throw err;
    } finally {
      if (options.showLoading !== false) {
        setIsLoading(false);
      }
    }
  }, [options]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isLoading,
    error,
    execute,
    clearError
  };
} 